import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TreatmentTemplateLibrary = ({ templates, onApplyTemplate, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');

  const specialties = ['all', 'General Dentistry', 'Orthodontics', 'Periodontics', 'Prosthodontics', 'Cosmetic Dentistry', 'Pediatric Dentistry'];

  const filteredTemplates = templates?.filter(template => {
    const matchesSearch = template?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         template?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesSpecialty = selectedSpecialty === 'all' || template?.specialty === selectedSpecialty;
    return matchesSearch && matchesSpecialty;
  });

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-3 border border-border max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Treatment Template Library</h2>
              <p className="text-sm text-muted-foreground mt-1">Select a template to start your treatment plan</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-smooth">
              <Icon name="X" size={24} className="text-muted-foreground" />
            </button>
          </div>

          {/* Search & Filter */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full"
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto">
              {specialties?.map((specialty) => (
                <button
                  key={specialty}
                  onClick={() => setSelectedSpecialty(specialty)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-smooth ${
                    selectedSpecialty === specialty
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {specialty === 'all' ? 'All' : specialty}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Template Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates?.map((template) => (
              <div
                key={template?.id}
                className="bg-background rounded-lg border border-border p-4 hover:shadow-elevation-2 transition-smooth"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                    <Icon name="FileText" size={24} className="text-primary" />
                  </div>
                  <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded">
                    {template?.specialty}
                  </span>
                </div>

                <h3 className="text-base font-semibold text-foreground mb-2">{template?.name}</h3>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{template?.description}</p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Duration:</span>
                    <span className="font-medium text-foreground">{template?.estimatedDuration}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Cost Range:</span>
                    <span className="font-medium text-foreground">{template?.averageCost}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Phases:</span>
                    <span className="font-medium text-foreground">{template?.phases} phases</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Procedures:</span>
                    <span className="font-medium text-foreground">{template?.procedures} procedures</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1"
                    iconName="Plus"
                    onClick={() => onApplyTemplate(template)}
                  >
                    Apply
                  </Button>
                  <Button variant="outline" size="sm" iconName="Eye">
                    Preview
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {filteredTemplates?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No templates found matching your criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TreatmentTemplateLibrary;